import type React from "react";
import { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import Chatbot from "src/features/chatbot/components/Chatbot";
import type { ChatbotUiStage } from "src/features/chatbot/components/Chatbot";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";
import { useLoading } from "src/common/components/AppState/IsLoading";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import GptConversationLoader, {
  GPT_CONVERSATION_LOADER_COMPONENT_ID,
} from "src/features/chatbot/components/GptConversationLoader";
import RagAgentMultiSelect from "src/features/rag/components/RagAgentMultiSelect";
import {
  fetchAgentsThunk,
  getRagAgents,
  getRagAgentsStatus,
} from "src/features/rag/state/ragSlice";
import {
  setActiveGptConversation,
  getGptConversationsSortedByRecency,
  getActiveGptConversation,
  fetchGptConversations,
  resetChat,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
} from "src/features/chatbot/state/chatbotSlice";

interface ScenarioHelperWorkbenchProps {
  lockedDestinationProjectGroupId?: number;
  disableAutoResume?: boolean;
}

const ScenarioHelperWorkbench: React.FC<ScenarioHelperWorkbenchProps> = ({
  lockedDestinationProjectGroupId,
  disableAutoResume,
}) => {
  const dispatch = useAppDispatch();
  const userPreviousGptConversations = useAppSelector(getGptConversationsSortedByRecency);
  const userActiveGptConversation = useAppSelector(getActiveGptConversation);
  const ragAgentIds = useAppSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const agents = useAppSelector(getRagAgents);
  const agentsStatus = useAppSelector(getRagAgentsStatus);
  const [chatUiStage, setChatUiStage] = useState<ChatbotUiStage>("thread");
  const conversationLoadingState = useLoading(GPT_CONVERSATION_LOADER_COMPONENT_ID);
  const planPhase = userActiveGptConversation?.plan_phase ?? null;
  const hasPlan = Boolean(userActiveGptConversation?.conversation_metadata?.plan);
  const isConfigureStage = planPhase === "ready_to_generate" && chatUiStage === "configure";
  useTourView(
    "scenaria-workbench",
    planPhase !== "generating" && conversationLoadingState !== LoadingState.Loading,
    TOUR_VIEW_ANCHORS["scenaria-workbench"].filter((anchor) => {
      switch (anchor) {
        case "scenaria-workbench-agents":
          return true;
        case "scenaria-workbench-generate":
          return isConfigureStage;
        case "scenaria-workbench-skip-intake":
          return planPhase === "intake" && Boolean(userActiveGptConversation?.id);
        case "scenaria-workbench-plan-card":
          return hasPlan && !isConfigureStage;
        default:
          return !isConfigureStage;
      }
    }),
  );

  const handleConversationSelect = (conversationId: number) => {
    const selectedConversation = userPreviousGptConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (selectedConversation) {
      dispatch(setActiveGptConversation(selectedConversation));
    }
  };

  const preSelectSophieIfAvailable = () => {
    const sophie = agents.find((a) => a.is_shared && a.name.toLowerCase() === "sophie");
    if (sophie) {
      dispatch(setScenarioHelperRagAgentIds([sophie.id]));
    }
  };

  const handleResetChat = () => {
    void dispatch(resetChat({ cb: resetChatLocally })).then(() => {
      void dispatch(
        fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
      );
      preSelectSophieIfAvailable();
    });
  };

  const resetChatLocally = () => {
    setActiveGptConversation(null);
  };

  useEffect(() => {
    void dispatch(
      fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
    );
  }, [dispatch]);

  useEffect(() => {
    void dispatch(fetchAgentsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (userActiveGptConversation) return;
    if (agentsStatus !== "loaded") return;
    preSelectSophieIfAvailable();
  }, [userActiveGptConversation, agentsStatus, agents]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div
        className="grid min-h-0 w-full flex-1 overflow-hidden"
        style={{ gridTemplateColumns: "240px 1fr 280px" }}
      >
        <div className="bg-gray-100 px-3 pt-1 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
          <GptConversationLoader
            onConversationSelect={handleConversationSelect}
            onResetChat={handleResetChat}
          />
        </div>

        <div className="min-w-0 overflow-hidden">
          <Chatbot
            mode="scenarioHelper"
            activeConversation={userActiveGptConversation}
            lockedDestinationProjectGroupId={lockedDestinationProjectGroupId}
            disableAutoResume={disableAutoResume}
            onUiStageChange={setChatUiStage}
          />
        </div>

        <div
          className="bg-gray-50 border-l border-gray-200 p-4 pb-32 flex flex-col min-h-0 overflow-x-hidden overflow-y-hidden"
          data-tour="scenaria-workbench-agents"
        >
          <RagAgentMultiSelect
            fillHeight
            value={ragAgentIds}
            onChange={(ids) => dispatch(setScenarioHelperRagAgentIds(ids))}
          />
        </div>
      </div>
    </div>
  );
};

export default ScenarioHelperWorkbench;
